// In-chat connector connect card — pure helpers shared by the chat state (use-chat),
// the BFF history mapping (messages API), and the card UI (MessageRow/ConnectorConnectCard).
// The wire shape is the harness contract: the ASSISTANT reply of a turn whose ConnectApp
// tool minted a Composio OAuth link carries `metadata.connectorCard` (and the SSE
// `messageEnd` event carries the same card in-band, like `choices`). The reply TEXT
// deliberately carries no URL — the card is the only way to connect.
//
// Deliberately dependency-free (mirrors lib/message-choices.ts): unit tests must not
// drag the common-lib graph, so the validation here MIRRORS common-lib's
// `readConnectorCard` — including the https-only connectUrl rule — rather than
// importing it. Keep the two in step.

export type ConnectorCardData = {
  /** Composio toolkit slug, e.g. `gmail` — matched against the connectors status data. */
  toolkit: string;
  /** Display name, e.g. `Gmail`. */
  name: string;
  /** Brand logo URL. Absent → the Composio hosted logo, then the letter tile. */
  logoUrl?: string;
  /** The minted https connect link the Connect button opens. */
  connectUrl: string;
  /** ISO timestamp of the mint — drives the expired state. */
  mintedAt?: string;
  /** ISO instant the link dies. When present it replaces the 24h `mintedAt` rule (a GBP link is
   *  single-use and valid 15 minutes). Mirrors common-lib `ConnectorCardMetadata.expiresAt`. */
  expiresAt?: string;
  /** Which flow put the card on screen (`shopify-landing`), carried into the card's BI. */
  source?: string;
  /** The card replaces an existing connection; done only after a tap and a healthy row. Mirrors common-lib. */
  reconnect?: true;
};

/**
 * Read + sanitize `metadata.connectorCard` off a raw message entity. A card missing its
 * required fields renders nothing rather than a broken card, and `connectUrl` must be
 * https so a corrupted marker can't become a tappable non-web URL.
 */
export function readConnectorCard(metadata: unknown): ConnectorCardData | undefined {
  const raw = (metadata as { connectorCard?: unknown } | undefined)?.connectorCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const { toolkit, name, logoUrl, connectUrl, mintedAt, expiresAt, source, reconnect } = raw as {
    toolkit?: unknown;
    name?: unknown;
    logoUrl?: unknown;
    connectUrl?: unknown;
    mintedAt?: unknown;
    expiresAt?: unknown;
    source?: unknown;
    reconnect?: unknown;
  };
  if (
    typeof toolkit !== 'string' ||
    !toolkit.trim() ||
    typeof name !== 'string' ||
    !name.trim() ||
    typeof connectUrl !== 'string' ||
    !/^https:\/\/\S+$/i.test(connectUrl)
  ) {
    return undefined;
  }
  return {
    toolkit,
    name,
    connectUrl,
    ...(typeof logoUrl === 'string' && /^https?:\/\/\S+$/i.test(logoUrl) ? { logoUrl } : {}),
    ...(typeof mintedAt === 'string' && mintedAt.trim() ? { mintedAt } : {}),
    ...(typeof expiresAt === 'string' && expiresAt.trim() ? { expiresAt } : {}),
    ...(typeof source === 'string' && /^[a-z0-9-]{1,40}$/.test(source) ? { source } : {}),
    ...(reconnect === true ? { reconnect: true as const } : {}),
  };
}

/** Live states, in precedence order: connected > expired > waiting > offered. */
export type ConnectorCardState = 'connected' | 'expired' | 'waiting' | 'offered';

/** A minted, still-unconnected link older than this offers a re-mint instead of the dead URL. */
export const CONNECT_LINK_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Derive the card's live state. `connected` always wins (however the connect happened —
 * this tab, another device, the settings screen). An un-connected link past its TTL is
 * `expired` even when tapped: the tab the tap opened can no longer succeed. A parseable
 * `expiresAt` is the link's own deadline and replaces the `mintedAt` + 24h rule; an
 * unparseable one falls back to that rule rather than fabricating an expiry.
 */
export function deriveConnectorCardState(input: {
  connected: boolean;
  tapped: boolean;
  mintedAt?: string;
  expiresAt?: string;
  /** The card's `reconnect`: the old connection already reads connected, so it counts only after a tap. */
  reconnect?: boolean;
  now: number;
}): ConnectorCardState {
  if (input.connected && (!input.reconnect || input.tapped)) return 'connected';
  const expires = input.expiresAt ? Date.parse(input.expiresAt) : NaN;
  if (Number.isFinite(expires)) {
    if (input.now > expires) return 'expired';
  } else if (input.mintedAt) {
    const minted = Date.parse(input.mintedAt);
    if (Number.isFinite(minted) && input.now - minted > CONNECT_LINK_TTL_MS) return 'expired';
  }
  if (input.tapped) return 'waiting';
  return 'offered';
}

/** The longest delay `setTimeout` honours (2^31 - 1 ms); a later deadline re-arms after the first wake. */
export const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * How long until the card's link dies, plus 1ms because expiry is strictly AFTER the deadline, so a wake
 * at this delay always lands on `expired`. Null when the card has no deadline or is already past it. The
 * deadline is `expiresAt` when parseable, else `mintedAt` + 24h: the rule `deriveConnectorCardState`
 * applies. It schedules the one re-render nothing else would: an idle chat has no poll and no new message,
 * so an `offered` card whose link dies on screen would otherwise keep a Connect that opens a dead link.
 * Mirrors iOS `ConnectorCardState.intervalUntilExpiry` and Android `connectorCardMsUntilExpiry`.
 */
export function msUntilConnectorCardExpiry(card: { mintedAt?: string; expiresAt?: string }, now: number): number | null {
  const expires = card.expiresAt ? Date.parse(card.expiresAt) : NaN;
  const minted = card.mintedAt ? Date.parse(card.mintedAt) : NaN;
  const deadline = Number.isFinite(expires) ? expires : Number.isFinite(minted) ? minted + CONNECT_LINK_TTL_MS : null;
  if (deadline === null) return null;
  const remaining = deadline - now;
  return remaining < 0 ? null : Math.min(remaining + 1, MAX_TIMER_DELAY_MS);
}

/**
 * localStorage key remembering that THIS browser opened the message's connect link —
 * what makes `waiting` survive a reload. Product-named (new keys never inherit the
 * legacy `orion-web:` prefix — see CLAUDE.md).
 */
export function connectTapStorageKey(messageId: string): string {
  return `symphony-connect-tap:${messageId}`;
}

/** While waiting, the connectors status is polled this often… */
export const CONNECT_POLL_INTERVAL_MS = 5_000;
/** …for at most this long after the tap (focus refetch keeps covering the long tail). */
export const CONNECT_POLL_WINDOW_MS = 3 * 60 * 1000;

/** Whether the 5s status poll is still inside its 3-minute window. */
export function shouldPollWhileWaiting(tappedAt: number, now: number): boolean {
  return now - tappedAt <= CONNECT_POLL_WINDOW_MS;
}

/**
 * Whether the connectors status data shows the card's toolkit as connected AND usable. A `needsReconnect` row
 * goes out `connected: true` so the Connectors screen draws its reconnect badge, but for this card it is the
 * opposite of done: a reconnect card on it read "Connected and ready" and offered no Connect at all.
 */
export function isToolkitConnected(
  connectors: readonly { toolkit?: string; connected?: boolean; needsReconnect?: boolean }[] | undefined,
  toolkit: string,
): boolean {
  if (!connectors) return false;
  const slug = toolkit.toLowerCase();
  return connectors.some((c) => c.connected === true && c.needsReconnect !== true && (c.toolkit ?? '').toLowerCase() === slug);
}

type Carded = { id?: string; finalId?: string; connectorCard?: ConnectorCardData };

/** Patch the matching row's connectorCard in place (matched by id OR persisted finalId); no-op when the row is gone. */
export function patchConnectorCardById<M extends Carded>(messages: M[], messageId: string, connectorCard: ConnectorCardData): M[] {
  const matches = (m: M) => m.id === messageId || m.finalId === messageId;
  if (!messages.some(matches)) return messages;
  return messages.map((m) => (matches(m) ? { ...m, connectorCard } : m));
}
